// components/portal/ActionCenter.tsx
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Target,
  Image as ImageIcon,
  FileText,
  ShieldAlert,
  CheckCircle,
  ArrowLeft,
  Loader2,
  Info,
  Building2,
  Layers,
} from '@/components/ui/lucide-memo';
import clsx from 'clsx';
import { ActionPriority, LeasingActionItem } from '#types/types';
import { OrganisationSelector } from './OrganisationSelector';
import { getActionPanelTitle } from '#lib/leasing/actionPanelTitles';

interface ActionGroupRowProps {
  actionType: LeasingActionItem['type'];
  label: string;
  lang: 'en' | 'fr';
  priority: ActionPriority;
  actions: LeasingActionItem[];
  onActionClick: (action: LeasingActionItem) => void;
}

const ACTION_CENTER_COPY = {
  en: {
    analyzingPortfolio: 'Analyzing portfolio...',
    portfolioUpToDate: 'Portfolio up to date',
    colCount: 'Count',
    colAction: 'Action',
    colPriority: 'Priority',
    colDescription: 'Description',
    breadcrumb: 'Admin / Actions',
    title: 'All actions',
  },
  fr: {
    analyzingPortfolio: 'Analyse du portefeuille...',
    portfolioUpToDate: 'Portefeuille a jour',
    colCount: 'Nombre',
    colAction: 'Action',
    colPriority: 'Priorite',
    colDescription: 'Description',
    breadcrumb: 'Admin / Actions',
    title: 'Toutes les actions',
  },
};

const deriveLocalizedActionTitle = (action: LeasingActionItem, lang: 'en' | 'fr'): string => {
  if (lang !== 'fr') return action.title;

  const localizedBase = getActionPanelTitle(action.type, 'fr');
  const separatorIndex = action.title.indexOf(':');
  if (separatorIndex === -1) {
    return localizedBase;
  }

  const suffix = action.title.slice(separatorIndex + 1).trim();
  if (!suffix) {
    return localizedBase;
  }
  return `${localizedBase} : ${suffix}`;
};

const extractSuffixAfterColon = (value: string): string | null => {
  const idx = value.indexOf(':');
  if (idx === -1) return null;
  const suffix = value.slice(idx + 1).trim();
  return suffix.length > 0 ? suffix : null;
};

const deriveLocalizedActionDescription = (action: LeasingActionItem, lang: 'en' | 'fr'): string => {
  if (lang !== 'fr') return action.description;

  const desc = action.description;

  const missingPropertiesMatch = /^(\d+)\s+properties are missing\s+\(Declared:\s*(\d+)\)\.?$/i.exec(desc);
  if (missingPropertiesMatch) {
    return `${missingPropertiesMatch[1]} actifs manquent (declares: ${missingPropertiesMatch[2]}).`;
  }

  const missingFloorsMatch = /^(\d+)\s+floors are missing based on declared count\s+\((\d+)\)\.?$/i.exec(desc);
  if (missingFloorsMatch) {
    return `${missingFloorsMatch[1]} etages manquent (declares: ${missingFloorsMatch[2]}).`;
  }

  const missingUnitsMatch = /^(\d+)\s+units are missing on this floor based on theoretical count\.?$/i.exec(desc);
  if (missingUnitsMatch) {
    return `${missingUnitsMatch[1]} lots manquent sur cet etage (compte theorique).`;
  }

  const floorPlanMissingMatch = /^The floor\s+"([^"]+)"\s+in\s+(.+)\s+has no plan image\.?$/i.exec(desc);
  if (floorPlanMissingMatch) {
    return `L etage "${floorPlanMissingMatch[1]}" dans ${floorPlanMissingMatch[2]} n a pas de plan.`;
  }

  const unitNoSurfaceMatch = /^Unit\s+(.+)\s+has no recorded surface area\.?$/i.exec(desc);
  if (unitNoSurfaceMatch) {
    return `Le lot ${unitNoSurfaceMatch[1]} n a aucune surface enregistree.`;
  }

  const unitNoPhotoMatch = /^Unit\s+(.+)\s+has no thumbnail image\.?$/i.exec(desc);
  if (unitNoPhotoMatch) {
    return `Le lot ${unitNoPhotoMatch[1]} n a pas de photo principale.`;
  }

  const candidatesReadyMatch = /^(\d+)\s+brands are ready for review\.?$/i.exec(desc);
  if (candidatesReadyMatch) {
    return `${candidatesReadyMatch[1]} enseignes sont pretes a etre revues.`;
  }

  const riskStatusMatch = /^Tenant is flagged as\s+(.+)\.\s+Immediate occupancy validation recommended\.?$/i.exec(desc);
  if (riskStatusMatch) {
    return `Le locataire est signale comme ${riskStatusMatch[1]}. Verification immediate recommandee.`;
  }

  const launchSourcingMatch = /^(\d+)\s+unit\(s\)\s+\((.+)\)\s+are vacant with no active leads\.?$/i.exec(desc);
  if (launchSourcingMatch) {
    return `${launchSourcingMatch[1]} lot(s) (${launchSourcingMatch[2]}) sont vacants sans lead actif.`;
  }

  const leaseEndMissingMatch = /^The lease for Unit\s+(.+)\s+is signed, but no expiration date is recorded\.?$/i.exec(desc);
  if (leaseEndMissingMatch) {
    return `Le bail du lot ${leaseEndMissingMatch[1]} est signe, mais la date de fin manque.`;
  }

  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
  switch (action.type) {
    case 'DECLARE_PORTFOLIO_SIZE':
      return 'Definissez le nombre d actifs du portefeuille pour activer les audits de couverture.';
    case 'DATA_GAP_NO_UNITS':
      return 'Cet actif ne contient encore aucun lot dans le systeme.';
    case 'DATA_GAP_NO_LEASING_MANAGER':
      return 'Cet actif est sans responsable leasing assigne.';
    case 'DATA_GAP_NO_FLOOR_PLAN':
      return 'Aucun plan d etage n est disponible.';
    case 'DATA_GAP_MISSING_UNITS':
      return 'Des lots manquent sur cet etage par rapport au nombre attendu.';
    case 'DATA_GAP_MISSING_FLOORS':
      return 'Des etages manquent par rapport au nombre declare.';
    case 'DATA_GAP_NO_FLOOR_CALIBRATION':
      return 'Le plan est charge mais non calibre. Les surfaces ne peuvent pas etre calculees.';
    case 'DATA_GAP_NO_UNIT_SURFACE':
      return 'La surface de ce lot n est pas renseignee.';
    case 'DATA_GAP_NO_UNIT_POSITION':
      return 'Le lot doit etre rattache et mappe sur un etage.';
    case 'DATA_GAP_NO_MAIN_PHOTO':
      return 'Aucune photo principale n est definie.';
    case 'RISK_ALERT_TENANT':
      return 'Le niveau de risque locataire est eleve. Verifiez la situation rapidement.';
    case 'VALIDATE_UNIT_STATUS':
      return 'Verifiez et actualisez le statut de ce lot.';
    case 'DATA_GAP_NO_LEASE_END_DATE':
      return 'La date de fin de bail est manquante.';
    case 'STALE_DEAL':
      return 'Aucune activite recente sur ce deal.';
    case 'IDENTIFY_ASSET_CANDIDATES':
      return 'Certaines marques n ont pas encore été évaluées récemment.';
    case 'REVIEW_CANDIDATES':
      return 'Des leads sont prets pour revue.';
    case 'SELECT_INCENTIVE_TEMPLATE':
      return 'Choisissez le modele d incitation avant les decisions financieres.';
    case 'SELECT_LOI_TEMPLATE':
      return 'Choisissez le modele LOI avant la generation de la lettre d intention.';
    case 'DECIDE_BASE_RENT': {
      const unit = extractSuffixAfterColon(action.title);
      return unit
        ? `Definissez le loyer facial annuel pour ${unit}.`
        : 'Definissez le loyer facial annuel.';
    }
    case 'DECIDE_LEASE_DURATION': {
      const unit = extractSuffixAfterColon(action.title);
      return unit
        ? `Definissez la duree ferme du bail pour ${unit}.`
        : 'Definissez la duree ferme du bail.';
    }
    case 'DECIDE_RENT_FREE_PERIOD':
      return 'Definissez la duree de franchise de loyer.';
    case 'DECIDE_FITOUT_CONTRIBUTION':
      return 'Definissez la participation bailleur aux travaux.';
    case 'DECIDE_STEP_RENTS':
      return 'Les loyers progressifs sont actifs mais non definis.';
    case 'VALIDATE_INCENTIVE_COVENANT':
      return 'Verifiez la coherence entre incitations, valeur du contrat et risque locataire.';
    case 'ESTIMATE_TENANT_FITOUT_COST':
      return 'Estimez le budget travaux du locataire pour calibrer votre participation.';
    case 'REDACT_INCENTIVE_LETTER':
      return 'Les prerequis sont valides. Generez le dossier d incitation.';
    case 'REDACT_LOI':
      return 'Les prerequis sont valides. Generez la lettre d intention.';
    case 'SELECT_DEAL_CONTACT':
      return 'Aucun contact decisionnaire n est enregistre pour cette negotiation.';
    default:
      return desc;
  }
};

const GROUP_PRIORITY_WEIGHT: Record<ActionPriority, number> = {
  CRITICAL: 3,
  WARNING: 2,
  OPPORTUNITY: 1,
  INFO: 0,
};

const PRIORITY_STYLES: Record<ActionPriority, { bg: string; border: string; text: string; label: { en: string; fr: string } }> = {
  CRITICAL: {
    bg: 'bg-[#FF7369]/10',
    border: 'border-[#FF7369]/20',
    text: 'text-[#FF7369]',
    label: { en: 'Critical', fr: 'Critique' },
  },
  WARNING: {
    bg: 'bg-[#FFA344]/10',
    border: 'border-[#FFA344]/20',
    text: 'text-[#FFA344]',
    label: { en: 'Warning', fr: 'Alerte' },
  },
  OPPORTUNITY: {
    bg: 'bg-[#B3D9FF]/10',
    border: 'border-[#B3D9FF]/20',
    text: 'text-[#B3D9FF]',
    label: { en: 'Opportunity', fr: 'Opportunite' },
  },
  INFO: {
    bg: 'bg-white/5',
    border: 'border-white/10',
    text: 'text-white/60',
    label: { en: 'Info', fr: 'Info' },
  },
};

const getActionIcon = (type: string, size = 14) => {
  switch (type) {
    case 'DATA_GAP_NO_LEASING_MANAGER':
    case 'DATA_GAP_MISSING_PROPERTIES':
      return <Building2 size={size} />;
    case 'DATA_GAP_NO_FLOOR_PLAN':
    case 'DATA_GAP_NO_FLOOR_CALIBRATION':
    case 'DATA_GAP_MISSING_UNITS':
    case 'DATA_GAP_MISSING_FLOORS':
      return <Layers size={size} />;
    case 'DATA_GAP_NO_UNIT_SURFACE':
      return <FileText size={size} />;
    case 'DATA_GAP_NO_UNIT_POSITION':
      return <Target size={size} />;
    case 'DATA_GAP_NO_MAIN_PHOTO':
      return <ImageIcon size={size} />;
    case 'LAUNCH_SOURCING':
      return <Target size={size} />;
    case 'REVIEW_CANDIDATES':
      return <CheckCircle size={size} />;
    case 'GENERATE_DOCUMENT':
    case 'SELECT_INCENTIVE_TEMPLATE':
    case 'SELECT_LOI_TEMPLATE':
    case 'DECIDE_BASE_RENT':
    case 'DECIDE_LEASE_DURATION':
    case 'DECIDE_RENT_FREE_PERIOD':
    case 'DECIDE_FITOUT_CONTRIBUTION':
    case 'DECIDE_STEP_RENTS':
    case 'REDACT_INCENTIVE_LETTER':
    case 'REDACT_LOI':
      return <FileText size={size} />;
    case 'VALIDATE_INCENTIVE_COVENANT':
      return <ShieldAlert size={size} />;
    case 'ESTIMATE_TENANT_FITOUT_COST':
      return <Info size={size} />;
    case 'VALIDATE_UNIT_STATUS':
      return <Info size={size} />;
    case 'RISK_ALERT_TENANT':
      return <ShieldAlert size={size} />;
    default:
      return <AlertCircle size={size} />;
  }
};

// Grid template: [action] [count] [priority] [description]
const ROW_GRID = 'grid-cols-[minmax(280px,1.2fr)_80px_140px_minmax(320px,2fr)]';

const ROW_STYLE = { minHeight: 48 };
const CONTAINER_STYLE = { fontFamily: 'ui-sans-serif, -apple-system, system-ui, sans-serif' };

interface ActionDetailRowProps {
  action: LeasingActionItem;
  actionType: LeasingActionItem['type'];
  lang: 'en' | 'fr';
  priorityStyle: (typeof PRIORITY_STYLES)[ActionPriority];
  onActionClick: (action: LeasingActionItem) => void;
}

const ActionDetailRow = React.memo(({ action, actionType, lang, priorityStyle, onActionClick }: ActionDetailRowProps) => {
  const title = deriveLocalizedActionTitle(action, lang);
  const description = deriveLocalizedActionDescription(action, lang);

  const handleClick = useCallback(() => {
    onActionClick(action);
  }, [onActionClick, action]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={clsx(
        'group grid w-full items-center border-b border-[#2A2A2A] bg-[#141414] text-left transition-colors hover:bg-[#1a1a1a]',
        ROW_GRID,
      )}
      style={ROW_STYLE}
    >
      <div className="flex h-full min-w-0 items-center gap-2 border-r border-[#2A2A2A] px-4 pl-12">
        <div className={clsx('flex h-5 w-5 flex-none items-center justify-center rounded-md border', priorityStyle.bg, priorityStyle.border, priorityStyle.text)}>
          {getActionIcon(actionType, 11)}
        </div>
        <span className="truncate text-[13px] font-normal text-white/85 transition-colors group-hover:text-white">{title}</span>
      </div>
      <div className="flex h-full items-center justify-center border-r border-[#2A2A2A] px-4">
        <span className="text-[11px] text-white/25">{'\u2014'}</span>
      </div>
      <div className="flex h-full items-center border-r border-[#2A2A2A] px-4">
        <span className={clsx('inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium capitalize', priorityStyle.bg, priorityStyle.border, priorityStyle.text)}>
          {priorityStyle.label[lang]}
        </span>
      </div>
      <div className="flex h-full min-w-0 items-center px-4">
        <span className="truncate text-[12px] text-white/55 transition-colors group-hover:text-white/75">{description}</span>
      </div>
    </button>
  );
});
ActionDetailRow.displayName = 'ActionDetailRow';

const ActionGroupRow = React.memo(({ actionType, label, lang, priority, actions, onActionClick }: ActionGroupRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const count = actions.length;
  const priorityStyle = PRIORITY_STYLES[priority];

  const handleToggle = useCallback(() => {
    setIsExpanded(previous => !previous);
  }, []);

  if (count === 0) return null;

  const groupSummary = lang === 'fr'
    ? `${count} ${count > 1 ? 'elements necessitent votre attention' : 'element necessite votre attention'}`
    : `${count} ${count > 1 ? 'items need your attention' : 'item needs your attention'}`;

  return (
    <div className="flex flex-col">
      {/* Group header row */}
      <button
        type="button"
        onClick={handleToggle}
        className={clsx(
          'grid w-full items-center border-b border-[#2A2A2A] bg-[#181818] text-left transition-colors hover:bg-[#1e1e1e]',
          ROW_GRID,
          isExpanded && 'bg-[#1c1c1c]',
        )}
        style={ROW_STYLE}
      >
        <div className="flex h-full items-center gap-2 border-r border-[#2A2A2A] px-4">
          <span className="flex h-4 w-4 flex-none items-center justify-center text-white/40">
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
          <div className={clsx('flex h-6 w-6 flex-none items-center justify-center rounded-md border', priorityStyle.bg, priorityStyle.border, priorityStyle.text)}>
            {getActionIcon(actionType, 12)}
          </div>
          <span className="truncate text-[13px] font-medium text-white/85">{label}</span>
        </div>
        <div className="flex h-full items-center justify-center border-r border-[#2A2A2A] px-4">
          <span className="font-mono text-[13px] tabular-nums text-white/55">{count}</span>
        </div>
        <div className="flex h-full items-center border-r border-[#2A2A2A] px-4">
          <span className={clsx('inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium capitalize', priorityStyle.bg, priorityStyle.border, priorityStyle.text)}>
            {priorityStyle.label[lang]}
          </span>
        </div>
        <div className="flex h-full items-center px-4">
          <span className="truncate text-[12px] text-white/45">{groupSummary}</span>
        </div>
      </button>

      {/* Expanded child rows */}
      {isExpanded && actions.map((action) => (
        <ActionDetailRow
          key={`${actionType}-${action.entityType || 'Global'}-${action.entityId || 'None'}`}
          action={action}
          actionType={actionType}
          lang={lang}
          priorityStyle={priorityStyle}
          onActionClick={onActionClick}
        />
      ))}
    </div>
  );
});
ActionGroupRow.displayName = 'ActionGroupRow';

export const ActionCenter = () => {
  const { kernel } = useKernel();
  const actions = useKernelStore(state => state.leasingActions);
  const isLoading = useKernelStore(state => state.isLeasingActionsLoading);
  const dbUserId = useKernelStore(state => state.userContext.dbUserId);
  const userContext = useKernelStore(state => state.userContext);
  const userPreferences = useKernelStore(state => state.userPreferences);
  const lang = useKernelStore(state => state.language);
  const selectedOrgId = userPreferences.commanderOrganisationId ?? null;
  const t = ACTION_CENTER_COPY[lang];

  useEffect(() => {
    if (dbUserId) {
      kernel.current?.fetchLeasingActions(selectedOrgId || undefined);
    }
  }, [dbUserId, selectedOrgId, kernel]);

  const handleOrganisationSelect = useCallback((orgId: string | null) => {
    if (!kernel.current) return;
    void kernel.current.updateAndSaveUserPreference('commanderOrganisationId', orgId);
  }, [kernel]);

  const handleActionClick = useCallback((action: LeasingActionItem) => {
    if (!kernel.current) return;

    void kernel.current.triggerActionUI({
      type: action.type,
      entityType: action.entityType,
      entityId: action.entityId,
    });
  }, [kernel]);

  const groupedActions = useMemo(() => {
    const groups: Record<string, LeasingActionItem[]> = {};

    actions.forEach(action => {
      const group = groups[action.type] ?? [];
      group.push(action);
      groups[action.type] = group;
    });

    return Object.entries(groups)
      .filter(([, items]) => items.length > 0)
      .map(([type, items]) => {
        const actionType = type as LeasingActionItem['type'];
        return {
          actionType,
          label: getActionPanelTitle(actionType, lang),
          items,
          priority: items[0]?.priority ?? 'INFO',
        };
      })
      .sort((a, b) => GROUP_PRIORITY_WEIGHT[b.priority] - GROUP_PRIORITY_WEIGHT[a.priority]);
  }, [actions, lang]);

  const handleBack = useCallback(() => {
    const k = kernel.current;
    if (!k) return;
    const sessionId = k.getActiveChatSessionId();
    if (!sessionId) return;
    const moved = k.goToPreviousSubject(sessionId);
    if (!moved) {
      k.setActiveSubject(sessionId, null);
    }
  }, [kernel]);

  return (
    <div className="flex h-full w-full flex-col bg-black" style={CONTAINER_STYLE}>
      {/* Top header bar, matches Table.tsx layout */}
      <div className="h-[56px] flex-none border-b border-[#2A2A2A] px-4">
        <div className="flex h-full items-center gap-3">
          <button
            onClick={handleBack}
            className="inline-flex h-[24px] w-[24px] items-center justify-center text-white transition-colors hover:text-white"
          >
            <ArrowLeft size={16} strokeWidth={2.4} />
          </button>
          <span className="h-5 w-[2px] bg-white/28" />
          <div className="flex min-w-0 flex-col gap-[2px] justify-center">
            <span className="text-[14px] leading-[14px] font-normal text-white/78">{t.breadcrumb}</span>
            <h1 className="text-[14px] font-normal leading-[14px] text-white">{t.title}</h1>
          </div>

          {userContext.isFaterAdmin && !userContext.isImpersonating && (
            <div className="ml-auto flex items-center gap-2 rounded-md border border-[#2A2A2A] bg-[#181818] px-2 py-1 transition-colors hover:bg-[#1e1e1e]">
              <Building2 size={12} className="text-white/40" />
              <OrganisationSelector
                selectedOrgId={selectedOrgId}
                onSelect={handleOrganisationSelect}
              />
            </div>
          )}
        </div>
      </div>

      {/* Table body (full width, scrollable) */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && actions.length === 0 ? (
          <div className="flex items-center justify-center gap-3 border-b border-[#2A2A2A] bg-[#181818] p-12 text-white/30">
            <Loader2 className="animate-spin" size={14} />
            <span className="text-[12px]">{t.analyzingPortfolio}</span>
          </div>
        ) : actions.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 border-b border-[#2A2A2A] bg-[#181818] p-12">
            <CheckCircle size={20} className="text-white/20" />
            <span className="text-[12px] text-white/30">{t.portfolioUpToDate}</span>
          </div>
        ) : (
          <div className="flex w-full flex-col bg-[#181818]">
            {/* Column header row */}
            <div className={clsx('sticky top-0 z-10 grid h-10 border-b border-[#2A2A2A] bg-[#1D1D1D] text-[13px] font-normal text-white/55', ROW_GRID)}>
              <div className="flex items-center border-r border-[#2A2A2A] px-4">{t.colAction}</div>
              <div className="flex items-center justify-center border-r border-[#2A2A2A] px-4">{t.colCount}</div>
              <div className="flex items-center border-r border-[#2A2A2A] px-4">{t.colPriority}</div>
              <div className="flex items-center px-4">{t.colDescription}</div>
            </div>

            {/* Body */}
            <div>
              {groupedActions.map(group => (
                <ActionGroupRow
                  key={group.actionType}
                  actionType={group.actionType}
                  label={group.label}
                  lang={lang}
                  priority={group.priority}
                  actions={group.items}
                  onActionClick={handleActionClick}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
